/**
 * Workspace themes — mockup parity (app.html APP_THEMES, ~line 8910).
 *
 * Each theme = a trading-room background image + an atmosphere overlay
 * (stacked radial gradients that tint the room without changing UI tokens)
 * + a cursor-spotlight tint. Applied by setting CSS vars on <html> so they
 * override the .dark defaults in globals.css; #app-layout-parent composites
 * `var(--app-bg-atmosphere), var(--app-bg-image)` (see app-layout.tsx).
 * Persisted in localStorage under `sv_workspace_theme` — same key as the
 * mockup so a designer flipping between the two keeps their room.
 */

export interface WorkspaceTheme {
  id: string;
  label: string;
  src: string;
  atmosphere: string;
  spot: string;
}

export const WORKSPACE_THEMES: WorkspaceTheme[] = [
  {
    id: "rain-penthouse",
    label: "Rain Penthouse",
    src: "/images/media/bg-options/03-rain-penthouse.webp",
    atmosphere:
      "radial-gradient(ellipse 80% 55% at 12% 48%,rgba(255,176,96,.075),transparent 58%),radial-gradient(ellipse 75% 60% at 88% 40%,rgba(96,168,255,.085),transparent 55%),radial-gradient(ellipse 85% 65% at 50% 58%,rgba(0,0,0,.22),transparent 70%),radial-gradient(ellipse at top,rgba(0,64,255,.04),transparent 48%)",
    spot: "rgba(255,196,140,.08)",
  },
  {
    id: "executive-desk",
    label: "Executive Desk",
    src: "/images/media/bg-options/05-executive-desk-split.webp",
    atmosphere:
      "radial-gradient(ellipse 70% 55% at 8% 52%,rgba(0,255,204,.09),transparent 58%),radial-gradient(ellipse 72% 58% at 94% 44%,rgba(72,148,255,.1),transparent 56%),radial-gradient(ellipse 88% 62% at 50% 60%,rgba(0,0,0,.2),transparent 72%),radial-gradient(ellipse at top,rgba(0,64,255,.045),transparent 50%)",
    spot: "rgba(0,255,204,.09)",
  },
  {
    id: "skyline-office",
    label: "Skyline Office",
    src: "/images/media/bg-options/01-skyline-trading-office.webp",
    atmosphere:
      "radial-gradient(ellipse 68% 58% at 6% 50%,rgba(0,255,204,.08),transparent 56%),radial-gradient(ellipse 68% 58% at 94% 50%,rgba(0,255,204,.08),transparent 56%),radial-gradient(ellipse 90% 68% at 50% 55%,rgba(0,0,0,.24),transparent 70%),radial-gradient(ellipse at bottom right,rgba(0,255,204,.035),transparent 55%)",
    spot: "rgba(0,255,204,.085)",
  },
  {
    id: "war-room",
    label: "War Room",
    src: "/images/media/bg-options/02-monitor-war-room.webp",
    atmosphere:
      "radial-gradient(ellipse 75% 70% at 4% 50%,rgba(0,220,255,.1),transparent 58%),radial-gradient(ellipse 75% 70% at 96% 50%,rgba(0,220,255,.1),transparent 58%),radial-gradient(ellipse 95% 75% at 50% 62%,rgba(0,0,0,.32),transparent 74%),radial-gradient(ellipse at top,rgba(0,180,255,.05),transparent 45%)",
    spot: "rgba(0,220,255,.09)",
  },
  {
    id: "cyber-suite",
    label: "Cyber Suite",
    src: "/images/media/bg-options/04-cyber-trading-suite.webp",
    atmosphere:
      "radial-gradient(ellipse 120% 45% at 50% 0%,rgba(0,255,136,.07),transparent 52%),radial-gradient(ellipse 120% 40% at 50% 100%,rgba(0,255,136,.055),transparent 50%),radial-gradient(ellipse 70% 60% at 92% 42%,rgba(180,220,255,.08),transparent 58%),radial-gradient(ellipse 88% 70% at 50% 58%,rgba(0,0,0,.26),transparent 72%)",
    spot: "rgba(0,255,136,.08)",
  },
  {
    id: "glass-lounge",
    label: "Glass Lounge",
    src: "/images/media/bg-options/06-glass-lounge-blue-hour.webp",
    atmosphere:
      "radial-gradient(ellipse 72% 58% at 8% 46%,rgba(110,160,255,.08),transparent 56%),radial-gradient(ellipse 72% 58% at 92% 46%,rgba(110,160,255,.08),transparent 56%),radial-gradient(ellipse at bottom right,rgba(0,255,204,.04),transparent 58%),radial-gradient(ellipse 85% 65% at 50% 60%,rgba(0,0,0,.18),transparent 72%)",
    spot: "rgba(130,180,255,.08)",
  },
];

export const WORKSPACE_THEME_KEY = "sv_workspace_theme";
export const WORKSPACE_THEME_DEFAULT = "executive-desk";

export function getWorkspaceTheme(): string {
  if (typeof window === "undefined") return WORKSPACE_THEME_DEFAULT;
  try {
    return localStorage.getItem(WORKSPACE_THEME_KEY) || WORKSPACE_THEME_DEFAULT;
  } catch {
    return WORKSPACE_THEME_DEFAULT;
  }
}

/** Apply a theme by id: sets the root CSS vars + data attribute, persists. */
export function applyWorkspaceTheme(id: string): WorkspaceTheme {
  const theme =
    WORKSPACE_THEMES.find((t) => t.id === id) ??
    WORKSPACE_THEMES.find((t) => t.id === WORKSPACE_THEME_DEFAULT)!;
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    root.style.setProperty("--app-bg-image", `url("${theme.src}")`);
    root.style.setProperty("--app-bg-atmosphere", theme.atmosphere);
    root.style.setProperty("--app-spot-tint", theme.spot);
    document.body.dataset.workspaceTheme = theme.id;
    try {
      localStorage.setItem(WORKSPACE_THEME_KEY, theme.id);
    } catch {
      /* private mode — theme still applies for the session */
    }
  }
  return theme;
}
